/// <amd-module name="Wit/Common/TeamServices/TeamSettingsService" />
import { DayOfWeek, ITeamSettings } from "Agile/Contracts/Common";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { TeamSettingsPatch } from "Wit/Clients/Work/Contracts/Work";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";

namespace TeamSettingsDataProviders {
    export const TeamSettingsDataProvider = "ms.vss-work-web.new-team-wit-settings-data-provider";
}

export interface ITeamSettingsPatch extends Partial<TeamSettingsPatch> { }

export interface ITeamSettingsService extends IVssService {
    getTeamSettings(options?: { ignoreCache: boolean }, teamId?: string): Promise<ITeamSettings | undefined>;
    getTeamSettingsObservable(options?: { ignoreCache: boolean, teamId?: string }): IReadonlyObservableValue<ITeamSettings | undefined>;
    updateTeamSettings(settings: ITeamSettingsPatch, teamId: string): Promise<void>;
}

class TeamSettingsService extends VssService implements ITeamSettingsService {
    private teamDataCache: ObservableValue<ITeamSettings | undefined>;
    private teamDataPromise: Promise<ITeamSettings | undefined>;

    public getTeamSettings(options?: { ignoreCache: boolean }, teamId?: string): Promise<ITeamSettings | undefined> {
        if (options?.ignoreCache || !this.teamDataPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.teamDataPromise = contributionService.getDataAsync<ITeamSettings>(
                TeamSettingsDataProviders.TeamSettingsDataProvider,
                {
                    teamId: teamId
                },
                options?.ignoreCache
            );
        }

        return this.teamDataPromise;
    }

    public getTeamSettingsObservable(options?: { ignoreCache: boolean, teamId?: string }): IReadonlyObservableValue<ITeamSettings | undefined> {
        if (options?.ignoreCache || !this.teamDataCache) {
            if (this.teamDataCache === undefined) {
                this.teamDataCache = new ObservableValue(undefined);
            }

            this.getTeamSettings(options, options?.teamId).then((teamSettings: ITeamSettings | undefined) => {
                this.teamDataCache.value = teamSettings;
            });
        }

        return this.teamDataCache;
    }

    public updateTeamSettings = (settings: ITeamSettingsPatch, teamId: string): Promise<void> => {
        const client = getWorkClient(this.pageContext);

        const tfsPageData = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        const projectId = tfsPageData?.project?.id ?? "";

        // the generated contracts are incorrect because some properties should be optional so need to do some casting here
        return client.updateTeamSettings(settings as unknown as TeamSettingsPatch, { teamId, projectId } as TeamContext).then(teamSettings => {
            if (this.teamDataCache?.value) {
                this.teamDataCache.value = {
                    ...this.teamDataCache.value,
                    backlogVisibilities: teamSettings.backlogVisibilities,
                    weekends: { canEditWeekends: this.teamDataCache.value.weekends.canEditWeekends, days: getWeekendDays(teamSettings.workingDays) },
                    bugsBehavior: Number(teamSettings.bugsBehavior)
                };
            }
        });
    };
}

function getWeekendDays(workDays: number[]) {
    const dayMap: { [id: string]: boolean } = {};

    dayMap[DayOfWeek.Sunday] = true;
    dayMap[DayOfWeek.Monday] = true;
    dayMap[DayOfWeek.Tuesday] = true;
    dayMap[DayOfWeek.Wednesday] = true;
    dayMap[DayOfWeek.Thursday] = true;
    dayMap[DayOfWeek.Friday] = true;
    dayMap[DayOfWeek.Saturday] = true;

    workDays.forEach(day => (dayMap[day] = false));
    return Object.keys(dayMap)
        .filter(day => dayMap[Number(day)])
        .map(day => Number(day));
}

Services.add("ITeamSettingsService", { serviceFactory: TeamSettingsService });
